import { merge } from "lodash"
import { BLANK_FUND_ID } from "../constants"
import {
  ConfigForValidation,
  State,
  ValidationErrorMessage,
  ValidationErrors,
} from "../types"
import { designationTotalCents } from "../utils/designationTotal"
import { formattedCents } from "source/giving/utils/money"

export function hasErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0
}

export function extractMessagesFrom(
  errors: ValidationErrors,
  ...searchKeys: string[]
): ValidationErrorMessage[] {
  const validKeys = Object.keys(errors)
  return validKeys
    .filter((vk) => searchKeys.some((searchKey) => vk.startsWith(searchKey)))
    .flatMap((k) => errors[k] || [])
}

export function validate(
  state: State,
  config: ConfigForValidation,
): ValidationErrors {
  return VALIDATORS.reduce((errors, validationFn) => {
    const result = validationFn(state, config)
    return merge(errors, result)
  }, {})
}

const VALIDATORS = [
  totalAmountAboveMinimum,
  totalAmountBelowMaximum,
  designationFundIdPresence,
  designationAmountPresence,
]

function totalAmountAboveMinimum(
  state: State,
  config: ConfigForValidation,
): ValidationErrors {
  const totalCents = designationTotalCents(state.designations)
  const allAmountsBlank = state.designations.every((d) => d.amount === "")
  if (!allAmountsBlank && totalCents < config.minimum_donation_amount_cents) {
    return {
      totalAmount: [
        `Amount must be at least ${formattedCents(config.minimum_donation_amount_cents)}`,
      ],
    }
  } else {
    return {}
  }
}

function totalAmountBelowMaximum(
  state: State,
  config: ConfigForValidation,
): ValidationErrors {
  const totalCents = designationTotalCents(state.designations)
  if (totalCents > config.maximum_donation_amount_cents) {
    return {
      totalAmount: [
        `Amount must be below ${formattedCents(config.maximum_donation_amount_cents)}`,
      ],
    }
  } else {
    return {}
  }
}

function designationFundIdPresence(state: State): ValidationErrors {
  const errors: ValidationErrors = {}
  state.designations.forEach((designation) => {
    if (designation.fundId === BLANK_FUND_ID) {
      errors[`designation_${designation.fundId}_fundId`] = ["Select a fund"]
    }
  })
  return errors
}

function designationAmountPresence(state: State): ValidationErrors {
  const errors: ValidationErrors = {}
  state.designations.forEach((designation) => {
    if (designation.amount === "") {
      errors[`designation_${designation.fundId}_amount`] = ["Enter an amount"]
    }
  })
  return errors
}
